import { z } from 'zod';
import { isoDateSchema, isoDateTimeSchema, uuidSchema, versionSchema, } from '../common/primitives.js';
export const GOAL_HORIZONS = ['QUARTERLY', 'ANNUAL', 'CUSTOM'];
export const goalHorizonSchema = z.enum(GOAL_HORIZONS);
export const GOAL_STATUSES = [
    'DRAFT',
    'ACTIVE',
    'AT_RISK',
    'ACHIEVED',
    'MISSED',
    'CANCELLED',
];
export const goalStatusSchema = z.enum(GOAL_STATUSES);
/** Closing statuses that must be explained (they become part of the goal timeline). */
export const GOAL_STATUSES_REQUIRING_NOTE = [
    'MISSED',
    'CANCELLED',
];
export const KEY_RESULT_STATUSES = [
    'ON_TRACK',
    'AT_RISK',
    'OFF_TRACK',
    'ACHIEVED',
    'MISSED',
];
export const keyResultStatusSchema = z.enum(KEY_RESULT_STATUSES);
/** Entities a goal can give direction to (spec §7.7); weekly updates link goals themselves. */
export const GOAL_LINK_TYPES = ['TOPIC', 'DECISION', 'TASK'];
export const goalLinkTypeSchema = z.enum(GOAL_LINK_TYPES);
/** Plain decimal with up to 4 fractional digits, as stored (`DECIMAL(20,4)`). */
export const krValueSchema = z
    .string()
    .trim()
    .regex(/^-?\d{1,16}(\.\d{1,4})?$/, 'Enter a number with at most 4 decimals');
/**
 * Display-only progress of a key result in percent (0–100), or `null` when start/target are not
 * both set or equal. Never used as a score of a person (spec §7.13).
 */
export function keyResultProgress(startValue, targetValue, currentValue) {
    if (startValue === null || targetValue === null)
        return null;
    const start = Number(startValue);
    const target = Number(targetValue);
    if (!Number.isFinite(start) || !Number.isFinite(target) || start === target)
        return null;
    const current = currentValue === null ? start : Number(currentValue);
    if (!Number.isFinite(current))
        return null;
    const ratio = (current - start) / (target - start);
    return Math.round(Math.min(1, Math.max(0, ratio)) * 100);
}
const memberRef = z.object({ memberId: uuidSchema, displayName: z.string() });
export const keyResultSchema = z.object({
    id: uuidSchema,
    title: z.string(),
    unit: z.string().nullable(),
    startValue: z.string().nullable(),
    targetValue: z.string().nullable(),
    currentValue: z.string().nullable(),
    status: keyResultStatusSchema,
    owner: memberRef.nullable(),
    progressPercent: z.number().int().min(0).max(100).nullable(),
    position: z.number().int(),
    version: z.number().int(),
});
export const goalSummarySchema = z.object({
    id: uuidSchema,
    title: z.string(),
    horizon: goalHorizonSchema,
    periodStart: isoDateSchema,
    periodEnd: isoDateSchema,
    status: goalStatusSchema,
    owner: memberRef,
    parentGoalId: uuidSchema.nullable(),
    keyResultCount: z.number().int(),
    /** Average progress of measurable key results; `null` when none is measurable. */
    progressPercent: z.number().int().min(0).max(100).nullable(),
    archived: z.boolean(),
    updatedAt: isoDateTimeSchema,
});
export const goalListSchema = z.object({ items: z.array(goalSummarySchema) });
export const goalListQuerySchema = z.object({
    /** Comma-separated statuses; default: everything except cancelled. */
    status: z
        .string()
        .optional()
        .transform((value, ctx) => {
        if (value === undefined || value === '')
            return undefined;
        const parsed = z.array(goalStatusSchema).safeParse(value.split(','));
        if (!parsed.success) {
            ctx.addIssue({ code: 'custom', message: 'Unknown goal status' });
            return z.NEVER;
        }
        return parsed.data;
    }),
    horizon: goalHorizonSchema.optional(),
    ownerMemberId: uuidSchema.optional(),
    includeArchived: z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
});
/** A linked item the reader can see; hidden items are counted but never described. */
export const goalLinkSchema = z.object({
    entityType: goalLinkTypeSchema,
    entityId: uuidSchema,
    label: z.string(),
    link: z.string(),
    status: z.string(),
});
export const GOAL_TIMELINE_KINDS = [
    'CREATED',
    'STATUS_CHANGED',
    'DECISION_FINALIZED',
    'TASK_COMPLETED',
    'UPDATE_SUBMITTED',
];
export const goalTimelineKindSchema = z.enum(GOAL_TIMELINE_KINDS);
export const goalTimelineEntrySchema = z.object({
    at: isoDateTimeSchema,
    kind: goalTimelineKindSchema,
    label: z.string(),
    note: z.string().nullable(),
    link: z.string().nullable(),
    actor: memberRef.nullable(),
});
export const goalDetailSchema = goalSummarySchema.extend({
    description: z.string().nullable(),
    parentGoal: z.object({ id: uuidSchema, title: z.string() }).nullable(),
    childGoals: z.array(z.object({ id: uuidSchema, title: z.string(), status: goalStatusSchema })),
    keyResults: z.array(keyResultSchema),
    links: z.array(goalLinkSchema),
    /** Linked items the reader is not allowed to see (restricted topics and their work). */
    hiddenLinkCount: z.number().int(),
    /** Submitted weekly updates that reference the goal (readers with `updates.read`). */
    updates: z.array(z.object({ id: uuidSchema, label: z.string(), link: z.string(), at: isoDateTimeSchema })),
    timeline: z.array(goalTimelineEntrySchema),
    canManage: z.boolean(),
    createdAt: isoDateTimeSchema,
    version: z.number().int(),
});
export const goalTitleSchema = z.string().trim().min(3).max(200);
export const keyResultInputSchema = z.object({
    title: goalTitleSchema,
    unit: z.string().trim().max(32).nullable().default(null),
    startValue: krValueSchema.nullable().default(null),
    targetValue: krValueSchema.nullable().default(null),
    ownerMemberId: uuidSchema.nullable().default(null),
});
function periodIsConsistent(value) {
    return value.periodEnd >= value.periodStart;
}
const goalFields = {
    title: goalTitleSchema,
    description: z.string().trim().max(5000).nullable().default(null),
    ownerMemberId: uuidSchema,
    horizon: goalHorizonSchema,
    periodStart: isoDateSchema,
    periodEnd: isoDateSchema,
    parentGoalId: uuidSchema.nullable().default(null),
};
export const createGoalRequestSchema = z
    .object({
    ...goalFields,
    status: z.enum(['DRAFT', 'ACTIVE']).default('ACTIVE'),
    keyResults: z.array(keyResultInputSchema).max(10).default([]),
})
    .refine(periodIsConsistent, {
    message: 'The period must end on or after its start',
    path: ['periodEnd'],
});
export const updateGoalRequestSchema = z
    .object({ ...goalFields, version: versionSchema })
    .refine(periodIsConsistent, {
    message: 'The period must end on or after its start',
    path: ['periodEnd'],
});
export const changeGoalStatusRequestSchema = z
    .object({
    version: versionSchema,
    status: goalStatusSchema,
    note: z.string().trim().max(2000).nullable().default(null),
})
    .refine((value) => !GOAL_STATUSES_REQUIRING_NOTE.includes(value.status) ||
    (value.note !== null && value.note.length >= 3), { message: 'Explain why the goal closes this way', path: ['note'] });
export const archiveGoalRequestSchema = z.object({ version: versionSchema, archived: z.boolean() });
export const addKeyResultRequestSchema = keyResultInputSchema.extend({ version: versionSchema });
export const updateKeyResultRequestSchema = keyResultInputSchema.extend({
    /** Key result version. */
    version: versionSchema,
    currentValue: krValueSchema.nullable().default(null),
    status: keyResultStatusSchema,
});
/** Removes a key result from the goal (kept in history; key results are never deleted). */
export const archiveKeyResultRequestSchema = z.object({ version: versionSchema });
export const goalLinkRequestSchema = z.object({
    entityType: goalLinkTypeSchema,
    entityId: uuidSchema,
});
/** Small picker list (active goals) for forms in other modules. */
export const goalOptionSchema = z.object({
    id: uuidSchema,
    title: z.string(),
    status: goalStatusSchema,
});
export const goalOptionListSchema = z.object({ items: z.array(goalOptionSchema) });
//# sourceMappingURL=goals.js.map